import styled, { css } from 'styled-components';

import { breakpoints, theme } from '@notino/react-styleguide';

import { CdnImage } from '@components/CdnImage';

import { NavLink } from './NavLink';

export interface IRowProps {
  isSelected: boolean;
  notInStock?: boolean;
  newDesign: boolean;
}

export const Name = styled.div<{ newDesign: boolean }>`
  flex-grow: 2;
  padding: 0.3125rem 0.625rem;
  flex-basis: 100%;

  @media (min-width: ${breakpoints.sm}) {
    flex-basis: auto;
  }
  @media (min-width: ${breakpoints.md}) {
    flex-basis: 100%;
  }
  @media (min-width: ${breakpoints.lg}) {
    flex-basis: auto;
  }

  ${({ newDesign }) =>
    newDesign
      ? css`
          display: flex;
          flex-direction: column-reverse;
          gap: 4px;
          font-size: 0.875rem !important;
        `
      : css`
          font-size: 1rem !important;
        `}
`;

export const Img = styled(CdnImage)<{ newDesign: boolean }>`
  overflow: hidden;

  ${({ newDesign }) =>
    newDesign
      ? css`
          object-fit: contain;
          width: 2.5rem;
          height: 2.5rem;
        `
      : css`
          max-width: 4.375rem !important;
          max-height: 4.375rem !important;
        `}
`;

export const Discount = styled.span<{ newDesign: boolean }>`
  color: ${theme.color.text.highlight};

  ${({ newDesign }) =>
    newDesign
      ? css`
          ${theme.typography.labelSmall}
        `
      : css`
          font-weight: bold;
          padding-left: 0.6rem;

          @media (min-width: ${breakpoints.sm}) {
            padding-left: 0;
          }
          @media (min-width: ${breakpoints.md}) {
            padding-left: 0.6rem;
          }
          @media (min-width: ${breakpoints.lg}) {
            padding-left: 0;
          }
        `}
`;

export const DiscountWrapper = styled.span`
  text-align: right;
  font-size: 0.9rem;
  line-height: 1.5rem;
  white-space: nowrap;
`;

export const LineThrough = styled.span<{
  active: boolean;
  newDesign: boolean;
  removeLeftPadding?: boolean;
}>`
  color: ${theme.color.text.secondary};
  ${({ active }) => active && 'text-decoration: line-through'};
  ${({ newDesign, removeLeftPadding }) =>
    newDesign
      ? css`
          ${theme.typography.labelSmall}
          color:${theme.color.text.tertiary};
          padding-left: ${removeLeftPadding ? '0' : '0.25rem'};
        `
      : css`
          padding-left: ${removeLeftPadding ? '0' : '0.6rem'};
        `}
`;

export const Row = styled(NavLink)<IRowProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  width: 100%;
  color: ${theme.color.text.primary};
  font-weight: ${(props) => (props.isSelected ? 'bold' : 'normal')};
  border-bottom: 1px solid ${theme.color.border.disabled};
  flex-grow: 1;
  opacity: ${(props) => (props.notInStock ? 0.7 : 1)};

  @media (min-width: ${breakpoints.md}) {
    :last-child {
      border-bottom: 0;
    }
  }

  &:hover {
    ${Name} {
      text-decoration: underline;
    }
  }

  ${({ newDesign }) =>
    newDesign
      ? css`
          padding: 0.75rem 1rem 0.75rem 0.75rem;
          min-height: 4rem;
        `
      : css`
          min-height: 5.0625rem;
        `}
`;

export const ImgWrapper = styled.span<{ newDesign: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;

  background-color: ${theme.color.background.primary};

  ${({ newDesign }) =>
    newDesign
      ? css`
          margin-right: 0.5rem;
        `
      : css`
          width: 5rem;
          height: 5rem;
        `}
`;

interface IPriceProps {
  isSelected: boolean;
  newDesign: boolean;
}

export const Price = styled.b<IPriceProps>`
  line-height: 1.5rem;
  font-size: 1.2rem;
  white-space: nowrap;
  font-weight: ${({ isSelected }) => (isSelected ? 'auto' : 'normal')};

  @media (min-width: ${breakpoints.sm}) {
    width: 100%;
    text-align: right;
  }

  @media (min-width: ${breakpoints.md}) {
    width: auto;
    text-align: left;
  }

  @media (min-width: ${breakpoints.lg}) {
    width: 100%;
    text-align: right;
  }

  ${({ newDesign, isSelected }) =>
    newDesign &&
    css`
      ${theme.typography.labelRegular}
      font-weight: ${isSelected ? 700 : 500};
      > span {
        font-size: 0.875rem !important;
      }
    `}
`;

export const PriceBesideBelow = styled.div<{ newDesign: boolean }>`
  display: flex;
  flex-shrink: 1;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;

  ${({ newDesign }) =>
    !newDesign &&
    css`
      flex-wrap: wrap;
      @media (min-width: ${breakpoints.md}) {
        flex-wrap: nowrap;
      }
    `}
`;

export const PriceWrapper = styled.span`
  flex-grow: 1;
  flex-shrink: 1;
  padding: 0 0.625rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex-wrap: wrap;
  align-items: flex-end;
  width: auto;
`;

export const PriceSymbol = styled.span`
  margin-left: 0.3rem;
  font-size: 1rem;
`;

export const DualPriceWrapper = styled.div`
  display: flex;
`;

export const Slash = styled.span<{ newDesign: boolean }>`
  padding: 0 0.25rem;
  ${({ newDesign }) =>
    newDesign
      ? css`
          line-height: 1.25rem;
        `
      : css`
          font-size: 19px;
          line-height: 1.5rem;
        `}
`;

export const RrpSlash = styled.span<{ newDesign: boolean }>`
  padding: 0.2rem;

  ${({ newDesign }) =>
    newDesign
      ? css`
          ${theme.typography.labelSmall}
          color:${theme.color.text.tertiary};
        `
      : css`
          color: ${theme.color.text.secondary};
        `}
`;
